<template>
  <v-dialog
    :value="visible"
    max-width="460"
    retain-focus
    content-class="feedback-prompt-dialog"
    @input="onDialogInput">
    <v-card class="fp" :aria-labelledby="titleId">
      <!--
        Header belongs to the form state only. Once answered, the question has no
        reason to stay on screen, and the X would be a second exit competing with
        the Close button.
      -->
      <div v-if="!result" class="fp__header">
        <div class="fp__heading">
          <h2 :id="titleId" class="fp__title">{{ copy.title }}</h2>
          <p class="fp__subtitle">{{ copy.subtitle }}</p>
        </div>
        <v-btn
          icon
          class="fp__close"
          :title="copy.closeTooltip"
          :aria-label="copy.closeTooltip"
          :disabled="busy"
          @click="snooze">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- --------------------------------------------------- terminal state -->
      <div v-if="result" class="fp__result" role="status">
        <span class="fp__result-badge" :class="`fp__result-badge--${result}`">
          <v-icon :color="resultIconColor" size="30">
            {{ result === 'success' ? 'mdi-check' : 'mdi-clock-outline' }}
          </v-icon>
        </span>
        <h2 :id="titleId" class="fp__result-title">{{ resultTitle }}</h2>
        <p class="fp__result-body">{{ resultBody }}</p>
        <v-btn class="fp__btn fp__btn--primary fp__result-btn" depressed @click="close">
          {{ copy.close }}
        </v-btn>
      </div>

      <!-- ------------------------------------------------------- form state -->
      <template v-else>
        <div class="fp__body">
          <!--
            Stars are native radios in a fieldset. Tab lands on the group once and
            arrow keys move between ratings - the browser's own behaviour, which no
            custom widget reproduces for free.
          -->
          <fieldset class="fp__field">
            <legend class="fp__label">
              {{ copy.ratingLegend }}
              <span class="fp__tag">{{ copy.ratingRequired }}</span>
            </legend>

            <div class="fp__stars" @mouseleave="hovered = 0">
              <template v-for="n in maxRating">
                <input
                  :id="starId(n)"
                  :key="`input-${n}`"
                  v-model.number="rating"
                  type="radio"
                  name="feedback-rating"
                  class="fp__star-input"
                  :value="n"
                  :disabled="busy" />
                <label
                  :key="`label-${n}`"
                  :for="starId(n)"
                  class="fp__star"
                  :class="{ 'is-on': n <= (hovered || rating) }"
                  @mouseenter="hovered = n">
                  <v-icon size="26">
                    {{ n <= (hovered || rating) ? 'mdi-star' : 'mdi-star-outline' }}
                  </v-icon>
                  <span class="ds-sr-only">{{ starLabel(n) }}</span>
                </label>
              </template>
            </div>

            <p :id="readoutId" class="fp__readout">{{ readout }}</p>
          </fieldset>

          <div class="fp__field">
            <label class="fp__label" :for="commentId">
              {{ copy.commentLabel }}
              <span class="fp__tag">{{ copy.commentOptional }}</span>
            </label>
            <textarea
              :id="commentId"
              v-model="comment"
              class="fp__textarea"
              rows="3"
              :maxlength="commentMaxLength"
              :placeholder="copy.commentPlaceholder"
              :disabled="busy"></textarea>
            <p v-if="showCounter" class="fp__counter" aria-hidden="true">
              {{ copy.charactersRemaining(charactersRemaining) }}
            </p>
            <!--
              Announced at thresholds only. A live region tied to every keystroke
              is unusable with a screen reader.
            -->
            <span class="ds-sr-only" role="status">{{ counterAnnouncement }}</span>
          </div>

          <div v-if="submitFailed" class="fp__error" role="alert">
            <v-icon size="18" color="error">mdi-alert-circle-outline</v-icon>
            <span>{{ copy.error }}</span>
          </div>
        </div>

        <!-- ---------------------------------------------------------- actions -->
        <div class="fp__actions">
          <v-btn class="fp__btn fp__btn--text" text :disabled="busy" @click="snooze">
            {{ copy.snooze }}
          </v-btn>
          <!--
            aria-disabled, not disabled: a disabled button leaves the tab order, so
            a keyboard user never meets the primary action and is never told why it
            is not ready.
          -->
          <v-btn
            class="fp__btn fp__btn--primary"
            depressed
            :class="{ 'is-not-ready': !hasRating }"
            :aria-disabled="String(!hasRating)"
            :aria-describedby="readoutId"
            :loading="busy"
            @click="onSubmitClick">
            {{ copy.submit }}
          </v-btn>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
  import {
    FEEDBACK_RATING,
    FEEDBACK_COMMENT_MAX_LENGTH,
    FEEDBACK_COUNTER_VISIBLE_FROM,
    FEEDBACK_COPY,
    FEEDBACK_RATING_LABELS,
  } from '@Feedback/constants/feedbackConstants';

  /** How long the terminal panel stays before closing itself. */
  const AUTO_CLOSE_MS = 4000;

  /** Remaining-character counts worth announcing, largest first. */
  const ANNOUNCE_AT = [100, 50, 20, 0];

  export default {
    name: 'FeedbackPromptDialog',

    data() {
      return {
        rating: null,
        hovered: 0,
        comment: '',
        counterAnnouncement: '',
        autoCloseTimer: null,
        lastAnnounced: null,
        /** Whatever the user was on when the prompt interrupted them. */
        returnFocusEl: null,
      };
    },

    computed: {
      visible() {
        return this.$store.state.FEEDBACK.visible;
      },
      busy() {
        return this.$store.state.FEEDBACK.busy;
      },
      submitFailed() {
        return this.$store.state.FEEDBACK.submitFailed;
      },
      result() {
        return this.$store.state.FEEDBACK.result;
      },
      isLowRating() {
        return this.$store.getters['FEEDBACK/IS_LOW_RATING'];
      },
      copy() {
        return FEEDBACK_COPY;
      },
      maxRating() {
        return FEEDBACK_RATING.MAX;
      },
      commentMaxLength() {
        return FEEDBACK_COMMENT_MAX_LENGTH;
      },
      hasRating() {
        return this.rating !== null;
      },
      titleId() {
        return 'feedback-prompt-title';
      },
      readoutId() {
        return 'feedback-prompt-readout';
      },
      commentId() {
        return 'feedback-prompt-comment';
      },
      resultTitle() {
        if (this.result !== 'success') return this.copy.snoozedTitle;
        return this.isLowRating ? this.copy.lowRatingTitle : this.copy.successTitle;
      },
      resultBody() {
        if (this.result !== 'success') return this.copy.snoozedBody;
        return this.isLowRating ? this.copy.lowRatingBody : this.copy.successBody;
      },
      resultIconColor() {
        return this.result === 'success' ? 'success' : 'primary';
      },
      charactersRemaining() {
        return this.commentMaxLength - this.comment.length;
      },
      showCounter() {
        return this.charactersRemaining <= FEEDBACK_COUNTER_VISIBLE_FROM;
      },
      readout() {
        if (!this.hasRating) return this.copy.ratingHint;
        return `${this.rating} - ${FEEDBACK_RATING_LABELS[this.rating]}`;
      },
    },

    watch: {
      visible(isVisible) {
        if (isVisible) {
          // v-dialog traps focus but does not give it back; without this the user
          // is returned to the top of the document after an interruption they did
          // not ask for.
          this.returnFocusEl = document.activeElement;
          this.reset();
        } else {
          this.restoreFocus();
        }
      },
      result(kind) {
        if (kind) this.startAutoClose();
      },
      charactersRemaining(remaining) {
        this.announceRemaining(remaining);
      },
    },

    beforeDestroy() {
      this.clearAutoClose();
    },

    methods: {
      starId(n) {
        return `feedback-prompt-star-${n}`;
      },

      starLabel(n) {
        const noun = n === 1 ? 'star' : 'stars';
        return `${n} ${noun} - ${FEEDBACK_RATING_LABELS[n]}`;
      },

      reset() {
        this.rating = null;
        this.hovered = 0;
        this.comment = '';
        this.counterAnnouncement = '';
        this.lastAnnounced = null;
        this.clearAutoClose();
      },

      /**
       * Only user-driven closes reach this: the dialog is bound with :value, so a
       * programmatic close never emits input. Esc and outside-click are both
       * dismissals, and every dismissal is a one-week snooze (spec Q14).
       */
      onDialogInput(open) {
        if (!open && !this.result) this.snooze();
      },

      onSubmitClick() {
        if (this.busy) return;
        if (!this.hasRating) {
          // Not an error, just not ready - send them where the answer is.
          const firstStar = document.getElementById(this.starId(1));
          if (firstStar) firstStar.focus();
          return;
        }
        this.$store.dispatch('FEEDBACK/SUBMIT', {
          rating: this.rating,
          comment: this.comment.trim(),
        });
      },

      snooze() {
        if (this.busy) return;
        this.$store.dispatch('FEEDBACK/SNOOZE');
      },

      close() {
        this.clearAutoClose();
        this.$store.dispatch('FEEDBACK/DISMISS');
      },

      startAutoClose() {
        this.clearAutoClose();
        this.autoCloseTimer = window.setTimeout(this.close, AUTO_CLOSE_MS);
      },

      clearAutoClose() {
        if (this.autoCloseTimer) {
          window.clearTimeout(this.autoCloseTimer);
          this.autoCloseTimer = null;
        }
      },

      restoreFocus() {
        const el = this.returnFocusEl;
        this.returnFocusEl = null;
        // The element may have been re-rendered away while the dialog was open.
        if (!el || !el.isConnected || typeof el.focus !== 'function') return;
        this.$nextTick(() => el.focus());
      },

      announceRemaining(remaining) {
        const threshold = ANNOUNCE_AT.find((t) => remaining <= t);
        if (threshold === undefined || threshold === this.lastAnnounced) return;
        this.lastAnnounced = threshold;
        this.counterAnnouncement = this.copy.charactersRemaining(remaining);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .fp {
    padding: 0;
  }

  // ----------------------------------------------------------------- header
  .fp__header {
    display: flex;
    align-items: flex-start;
    gap: var(--ds-space-sm, 8px);
    padding: 20px 12px 0 20px;
  }

  .fp__heading {
    flex: 1 1 auto;
    min-width: 0;
  }

  .fp__title {
    font-family: var(--ds-font-family-heading, 'Poppins'), sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0;
  }

  .fp__subtitle {
    font-size: 13px;
    color: var(--v-text-base);
    margin: 4px 0 0;
  }

  // 40px keeps the close button at a usable target; Vuetify's icon default is 36.
  .fp__close.v-btn--icon.v-size--default {
    width: 40px;
    height: 40px;
    margin-top: -4px;
  }

  // ------------------------------------------------------------------- body
  .fp__body {
    padding: 16px 20px 4px;
  }

  .fp__field {
    border: 0;
    padding: 0;
    margin: 0 0 18px;
  }

  .fp__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    padding: 0;
    margin-bottom: 6px;
  }

  // 11px, not the showcase's 10px: micro-type fails the 8-hour comfort test and
  // the design detector flags 10px as undersized UI text.
  // Deliberately not error-red - nothing has gone wrong, and red means alarm here.
  .fp__tag {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 1px 6px;
    border-radius: var(--ds-radius-sm, 3px);
    color: var(--v-text-base);
    background: rgba(0, 0, 0, 0.05);
  }

  // ------------------------------------------------------------------ stars
  .fp__stars {
    display: flex;
    gap: 2px;
  }

  .fp__star-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  .fp__star {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px; // >= 44px touch target
    height: 46px;
    border-radius: var(--ds-radius-sm, 4px);
    cursor: pointer;

    .v-icon {
      color: var(--ds-color-rating-empty);
    }

    &.is-on .v-icon {
      color: var(--ds-color-rating);
    }

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  }

  // The focus ring belongs on the label, because the input itself is hidden.
  .fp__star-input:focus-visible + .fp__star {
    outline: 2px solid var(--v-primary-base, #03624c);
    outline-offset: -2px;
  }

  .fp__readout {
    font-size: 12px;
    color: var(--v-text-base);
    margin: 4px 0 0;
    min-height: 18px;
  }

  // ---------------------------------------------------------------- comment
  .fp__textarea {
    width: 100%;
    resize: vertical;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    padding: 8px 10px;
    border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.14));
    border-radius: var(--ds-radius-sm, 4px);
    background: transparent;
    color: inherit;

    &:focus {
      outline: none;
      border-color: var(--v-primary-base, #03624c);
      box-shadow: 0 0 0 1px var(--v-primary-base, #03624c);
    }
  }

  .fp__counter {
    font-size: 12px;
    color: var(--v-text-base);
    text-align: right;
    margin: 4px 0 0;
  }

  .fp__error {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--v-error-base, #b71c1c);
    background: rgba(183, 28, 28, 0.08);
    border-radius: var(--ds-radius-sm, 4px);
    padding: 8px 10px;
    margin-bottom: 12px;
  }

  // ---------------------------------------------------------------- actions
  .fp__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 8px 20px 16px;
  }

  .fp__btn {
    height: 40px !important;
    letter-spacing: 0;
    text-transform: none;
  }

  .fp__btn--primary {
    background-color: var(--v-primary-base, #03624c) !important;
    color: #fff !important;
  }

  // "Not ready" rather than disabled: still visible, still focusable, and the
  // readout says what to do. Keeps the label contrast a faded button loses.
  .fp__btn--primary.is-not-ready {
    background-color: var(--ds-color-neutral, #9ca3af) !important;
  }

  // ----------------------------------------------------------- result state
  // The terminal state is a single centred column: badge, title, sentence,
  // action. It replaces the whole card, so there is nothing to align against
  // and centring reads as deliberate rather than as a half-filled form.
  .fp__result {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 32px 24px 24px;
  }

  .fp__result-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    margin-bottom: 14px;
  }

  .fp__result-badge--success {
    background: rgba(44, 194, 149, 0.14);
  }

  .fp__result-badge--snoozed {
    background: rgba(3, 98, 76, 0.1);
  }

  .fp__result-title {
    font-family: var(--ds-font-family-heading, 'Poppins'), sans-serif;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
    margin: 0;
  }

  .fp__result-body {
    font-size: 13px;
    line-height: 1.5;
    color: var(--v-text-base);
    margin: 6px 0 0;
    // Keeps the sentence from stretching the full 460px, which is what makes
    // centred text read as ragged.
    max-width: 36ch;
  }

  .fp__result-btn {
    margin-top: 20px;
    min-width: 128px;
  }

  @media (max-width: 560px) {
    .fp__actions {
      flex-direction: column-reverse;
      align-items: stretch;
    }
  }
</style>
